import { useEffect, useState } from 'react';

/**
 * Pull-down nav group (operator UI pass 2026-07-17): many tabs collapse
 * into compact accent-coloured menus. The button carries the group label +
 * the currently open tab's name when it belongs to the group; the list
 * closes on selection or outside click. Generic over the app's view union —
 * shared by reception.* and the bos.* superset cockpit (source export).
 */
export function NavMenu<V extends string>({
  label,
  accent,
  tabs,
  view,
  onGo,
}: {
  label: string;
  accent: string;
  tabs: { view: V; label: string }[];
  view: V;
  onGo: (v: V) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = tabs.find((t) => t.view === view);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  return (
    <div className={`app-menu${active ? ' is-current' : ''}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="app-menu-btn"
        style={{ ['--menu-accent' as string]: accent }}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="app-menu-label">{label}</span>
        <span className="app-menu-current">{active ? active.label : '…'}</span>
        <span className="app-menu-caret">▾</span>
      </button>
      {open && (
        <div className="app-menu-list">
          {tabs.map((t) => (
            <button
              key={t.view}
              type="button"
              className={view === t.view ? 'is-active' : ''}
              onClick={() => {
                setOpen(false);
                onGo(t.view);
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Single top-level nav button (no dropdown) — for views promoted out of a
 * group (Conciergerie, Planning: operator request 2026-07-23). Same visual
 * language as NavMenu's collapsed button, minus the caret/current-tab text.
 */
export function NavLink<V extends string>({
  label,
  accent,
  target,
  view,
  onGo,
}: {
  label: string;
  accent: string;
  target: V;
  view: V;
  onGo: (v: V) => void;
}) {
  const active = view === target;
  return (
    <button
      type="button"
      className={`app-menu-btn app-nav-link${active ? ' is-current' : ''}`}
      style={{ ['--menu-accent' as string]: accent }}
      onClick={() => onGo(target)}
    >
      <span className="app-menu-label">{label}</span>
    </button>
  );
}
